import { DeleteOutlined, MoreVert } from '@mui/icons-material';
import {
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material';
import { useDeleteGroupMutation } from 'api/ordering-service/ordering-service';
import { TransformGroupResult } from 'api/ordering-service/ordering-service.types';
import AuthorizationGate from 'components/authorization-gate/authorization-gate.component';
import ConfirmationDrawer from 'components/drawers/confirmation-drawer/confirmation-drawer.component';
import { useUtilityDrawer } from 'components/drawers/utility-drawer/utility-drawer.hooks';
import {
  bindMenu,
  bindTrigger,
  usePopupState,
} from 'material-ui-popup-state/hooks';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

type GroupsPopoverProps = {
  drawerData: Partial<TransformGroupResult>;
};

const GroupsPopover = ({ drawerData }: GroupsPopoverProps) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const deleteConfirmationDrawer = useUtilityDrawer();
  const [deleteGroup] = useDeleteGroupMutation();

  const groupPopupState = usePopupState({
    variant: 'popover',
    popupId: 'group-popup',
  });

  const onConfirmationValid = async () => {
    try {
      await deleteGroup({ groupId: drawerData?.groupId }).unwrap();

      enqueueSnackbar(
        t('GroupsDataGrid.actions.delete.alertMessage', {
          groupTitle: drawerData?.title,
        }),
        { severity: 'success' },
      );
      deleteConfirmationDrawer.close();
    } catch (_e) {}
  };

  return (
    <>
      <AuthorizationGate permission="DELETE_GROUP">
        <IconButton
          {...bindTrigger(groupPopupState)}
          data-testid="GroupInfo.IconButton"
          aria-label={t('forms.groupsForm.groupCard.actions.aria-label')}
        >
          <MoreVert />
        </IconButton>
      </AuthorizationGate>
      <Menu {...bindMenu(groupPopupState)}>
        <AuthorizationGate permission="DELETE_GROUP">
          <MenuItem
            onClick={() => {
              groupPopupState.close();
              deleteConfirmationDrawer.setOpen(true);
            }}
          >
            <ListItemIcon>
              <DeleteOutlined color="error" />
            </ListItemIcon>
            <ListItemText
              primary={t('forms.groupsForm.groupCard.actions.delete.label')}
            />
          </MenuItem>
        </AuthorizationGate>
      </Menu>
      <ConfirmationDrawer
        open={deleteConfirmationDrawer.open}
        close={deleteConfirmationDrawer.close}
        subtitle={t('drawer.groupDeleteDrawer.subtitle', {
          groupTitle: drawerData?.title,
        })}
        submitButtonLabel={t('drawer.groupDeleteDrawer.submitButtonLabel')}
        onValid={onConfirmationValid}
        cancel={deleteConfirmationDrawer.close}
        color="error"
      />
    </>
  );
};

export default GroupsPopover;
